import { memo, useMemo, type FC } from 'react';
import { styled } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Button } from '../../../Controls/Button/index.tsx';
import Icons from '../../../Elements/Icons/index.ts';
import { Select } from '../../Select/index.tsx';
import type { Option } from '../types.ts';

const MonthSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MonthSelectContainer = styled.div`
  flex: 1;
`;

interface MonthSelectProps {
  date: Date;
  changeMonth: (month: number) => void;
  changeYear: (year: number) => void;
  locale: string;
  dataTestId?: string;
}

export const MonthSelect: FC<MonthSelectProps> = memo(function MonthSelect({ date, changeMonth, changeYear, locale, dataTestId }) {
  const monthOptions = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });

    return Array.from({ length: 12 }, (_, index) => ({
      value: index,
      label: formatter.format(new Date(2000, index, 1)),
    }));
  }, [locale]);
  const value = useMemo(() => monthOptions[date.getMonth()], [date, monthOptions]);

  const handleChange = useCallbackRef((option: Option<number> | null) => {
    if (option) {
      changeMonth(option.value);
    }
  });

  const handlePrevMonth = useCallbackRef(() => {
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();

    if (currentMonth === 0) {
      changeMonth(11);
      changeYear(currentYear - 1);
    } else {
      changeMonth(currentMonth - 1);
    }
  });

  const handleNextMonth = useCallbackRef(() => {
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();

    if (currentMonth === 11) {
      changeMonth(0);
      changeYear(currentYear + 1);
    } else {
      changeMonth(currentMonth + 1);
    }
  });

  return (
    <MonthSelectWrapper>
      <Button onClick={handlePrevMonth} ghost iconOnly dataTestId={dataTestId ? `${dataTestId}-month-prev` : undefined}>
        <Icons.Chevron align='left' size={20} />
      </Button>
      <MonthSelectContainer>
        <Select<Option<number>>
          options={monthOptions}
          value={value}
          onChange={handleChange}
          dataTestId={dataTestId ? `${dataTestId}-month-select` : undefined}
        />
      </MonthSelectContainer>
      <Button onClick={handleNextMonth} ghost iconOnly dataTestId={dataTestId ? `${dataTestId}-month-next` : undefined}>
        <Icons.Chevron align='right' size={20} />
      </Button>
    </MonthSelectWrapper>
  );
});
